import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import api from '../services/api';

interface CustomerMinMaxCalcProps {
  open: boolean;
  onClose: () => void;
  selectedItems: any[];
  customerName: string;
  customerNumber: number;
  onComplete: () => void;
  storeName?: string;
}

interface CustomerMinMaxItem {
  manufacturer_id: number;
  manufacturer_name: string;
  medicine_name: string;
  pack_size: string;
  customer_max_quantity: number;
  sales_total_sold_60d: number;
  min: number;
  max: number;
}

const CustomerMinMaxCalc: React.FC<CustomerMinMaxCalcProps> = ({ 
  open, 
  onClose, 
  selectedItems, 
  customerName,
  customerNumber,
  onComplete,
  storeName
}: CustomerMinMaxCalcProps) => {
  const [items, setItems] = useState<CustomerMinMaxItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open && selectedItems.length > 0) {
      initializeItems();
    }
  }, [open, selectedItems]);

  const initializeItems = () => {
    const initializedItems = selectedItems.map((item: any) => {
      const sales60d = parseInt(item.sales_total_sold_60d) || 0;
      const customerMaxQty = parseInt(item.max_quantity) || 0;
      
      let calculatedMin = Math.round((sales60d / 60) * 21);
      let calculatedMax = Math.round((sales60d / 60) * 35);
      const threshold42Days = Math.round((sales60d / 60) * 42);

      // Special case: If store sales are ZERO but customer has refill need
      // Set min/max based purely on customer requirement
      if (sales60d === 0 && customerMaxQty > 0) {
        calculatedMin = customerMaxQty * 2; // At least 2x customer need
        calculatedMax = customerMaxQty * 3; // Up to 3x customer need
      } else {
        // Three-tier customer refill adjustment logic:
        
        // Tier 1: If calculated min is less than 2x customer max qty
        if (calculatedMin < (2 * customerMaxQty)) {
          calculatedMin = calculatedMin + (2 * customerMaxQty);
          calculatedMax = calculatedMax + (2 * customerMaxQty);
          // No cap for Tier 1 - customer needs take priority
        }
        // Tier 2: If calculated min is between 2x customer max qty and 42-day threshold
        else if (calculatedMin >= (2 * customerMaxQty) && calculatedMin < threshold42Days) {
          calculatedMin = calculatedMin + customerMaxQty;
          calculatedMax = calculatedMax + customerMaxQty;
          // No cap for Tier 2 - customer needs take priority
        }
        // Tier 3: If calculated min is already >= 42-day threshold
        else if (calculatedMin >= threshold42Days) {
          // Use calculated values as-is, but apply caps to prevent overstocking
          const threshold60Days = Math.round((sales60d / 60) * 60);
          
          if (threshold42Days > 0 && calculatedMin > threshold42Days) {
            calculatedMin = threshold42Days;
          }
          
          if (threshold60Days > 0 && calculatedMax > threshold60Days) {
            calculatedMax = threshold60Days;
          }
        }
      }

      return {
        manufacturer_id: item.id,
        manufacturer_name: item.manufacturer_name,
        medicine_name: item.medicine_name,
        pack_size: item.pack_size,
        customer_max_quantity: customerMaxQty,
        sales_total_sold_60d: sales60d,
        min: calculatedMin,
        max: calculatedMax,
      };
    });

    setItems(initializedItems);
  };

  const handleRecalculate = () => {
    initializeItems();
  };

  const handleMinChange = (index: number, value: string) => {
    const numValue = parseInt(value) || 0;
    setItems((prev: CustomerMinMaxItem[]) => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], min: numValue };
      return newItems;
    });
  };

  const handleMaxChange = (index: number, value: string) => {
    const numValue = parseInt(value) || 0;
    setItems((prev: CustomerMinMaxItem[]) => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], max: numValue };
      return newItems;
    });
  };

  const validateItems = (): string | null => {
    for (const item of items) {
      if (item.min > item.max) {
        return `Invalid min-max for ${item.medicine_name}: Min (${item.min}) cannot be greater than Max (${item.max})`;
      }
      if (item.min < 0 || item.max < 0) {
        return `Invalid min-max for ${item.medicine_name}: Values cannot be negative`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    setError(null);
    
    const validationError = validateItems();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      // Save min-max values to item_min_max table
      const payload = items.map((item: CustomerMinMaxItem) => ({
        manufacturer_id: item.manufacturer_id,
        min: item.min,
        max: item.max,
      }));

      // Note: We need store_id from localStorage like in Reports.tsx
      const savedStore = localStorage.getItem('selectedStore');
      let storeId: number | null = null;
      
      if (savedStore) {
        try {
          const store = JSON.parse(savedStore);
          storeId = store.id;
        } catch (error) {
          console.error('Error parsing saved store:', error);
        }
      }

      if (!storeId) {
        setError('Please select a store first from the top menu');
        setSaving(false);
        return;
      }

      // Add store_id to each item
      const itemsWithStore = payload.map((item: { manufacturer_id: number; min: number; max: number }) => ({
        ...item,
        store_id: storeId
      }));

      await api.post('/utilities/item-min-max/bulk-upsert', { items: itemsWithStore });

      // Save refills to customer table
      const refillsData = items.map((item: CustomerMinMaxItem) => ({
        id: item.manufacturer_id,
        medicine_name: item.medicine_name,
        manufacturer_name: item.manufacturer_name,
        pack_size: item.pack_size,
        max_quantity: item.customer_max_quantity,
      }));

      await api.post('/utilities/customer-refills/update', {
        customer_number: customerNumber,
        store_id: storeId,
        medicines: refillsData
      });

      setSuccess(true);
      setTimeout(() => {
        onComplete();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Error saving customer min-max:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save min-max values');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Customer Min-Max Calculator</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Successfully updated {items.length} items and saved refills!
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Customer:</strong> {customerName} ({customerNumber})
          </Typography>
          {storeName && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Store:</strong> {storeName}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Formula:</strong> Min = (Store Sales 60d ÷ 60) × 21 days | Max = (Store Sales 60d ÷ 60) × 35 days
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            <strong style={{ color: '#1976d2' }}>Special Case - Zero Sales:</strong> If store sales = 0 but customer has refills, Min = 2 × customer max qty, Max = 3 × customer max qty
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
            <strong>Customer Refill Adjustment (3 tiers):</strong>
            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
              <li>If calculated min &lt; 2 × customer max qty: Add 2 × customer max qty to both min and max</li>
              <li>If calculated min ≥ 2 × customer max qty AND &lt; 42-day threshold: Add customer max qty to both min and max</li>
              <li>If calculated min ≥ 42-day threshold: Use calculated values as-is</li>
            </ul>
            <strong style={{ color: '#d32f2f' }}>Cap:</strong> Min is capped at 42-day threshold, Max is capped at 60-day threshold to prevent overstocking
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Edit the values below as needed, then click Submit to save.
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Medicine Name</TableCell>
                <TableCell>Pack Size</TableCell>
                <TableCell align="right">Customer Max Qty</TableCell>
                <TableCell align="right">Store Sales 60d</TableCell>
                <TableCell align="right">Min</TableCell>
                <TableCell align="right">Max</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item: CustomerMinMaxItem, index: number) => (
                <TableRow key={item.manufacturer_id}>
                  <TableCell>{item.manufacturer_name}</TableCell>
                  <TableCell>{item.medicine_name}</TableCell>
                  <TableCell>{item.pack_size}</TableCell>
                  <TableCell align="right">{item.customer_max_quantity}</TableCell>
                  <TableCell align="right">{item.sales_total_sold_60d}</TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      value={item.min}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleMinChange(index, e.target.value)}
                      size="small"
                      sx={{ width: 80 }}
                      inputProps={{ min: 0 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      value={item.max}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleMaxChange(index, e.target.value)}
                      size="small"
                      sx={{ width: 80 }}
                      inputProps={{ min: 0 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRecalculate} disabled={saving}>
          Recalculate
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || items.length === 0}
          startIcon={saving && <CircularProgress size={20} />}
        >
          {saving ? 'Saving...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerMinMaxCalc;

